import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import useDidMountEffect from "../hooks/useDidMountEffect";
import _ from 'lodash'
import {useDebounce} from "@uidotdev/usehooks";
import {useModalsStore} from "../stores/modalsStore";
import {useDefaultQuoteSettingsContext} from "./DefaultQuoteSettingsContext";

export const CalcContext = createContext(undefined)

const initialCarState = {
    makes: [],
    models: [],
    trims: [],
    options: [],

    base_car_id: null,
    year: '',
    make: '',
    model: '',
    trim: '',
    option_codes: [],
    vin_required: null,
    vin_vehicles: [],
}

const getInitialCalcState = (quoteSettings) => {
    const customerLocation = quoteSettings.customer_location
    const dealerLocation = quoteSettings.dealer_location

    return {
        base_car_id: null,
        option_code_ids: [],
        msrp: '',
        msrp_plus: '',
        msrp_minus: '',
        msrp_percentage: '',

        invoice: '',
        invoice_plus: '',
        invoice_minus: '',

        cost: '',
        is_purchase: false,
        vin: '',

        term: quoteSettings.term,
        profit: quoteSettings.profit,
        mileage: quoteSettings.mileage,
        mileage_alt: quoteSettings.mileage_alt,
        drive_off: quoteSettings.drive_off,
        is_drive_off_cap_reduction: quoteSettings.is_drive_off_cap_reduction,

        location: customerLocation,
        location_id: customerLocation.id,
        dealer_location: dealerLocation,
        dealer_location_id: dealerLocation.id,

        address_line_one: customerLocation.address_line_one,
        address_line_two: customerLocation.address_line_two,
        city: customerLocation.city,
        state: customerLocation.state,
        zipcode: customerLocation.zipcode,
        county: customerLocation.county,

        dealer_address_line_one: dealerLocation.address_line_one,
        dealer_address_line_two: dealerLocation.address_line_two,
        dealer_city: dealerLocation.city,
        dealer_state: dealerLocation.state,
        dealer_zipcode: dealerLocation.zipcode,
        dealer_county: dealerLocation.county,

        current_mileage: 0,
        is_certified_preowned: true,

        rebates: []
    }
}

export const CalcContextProvider = ({ children, id } ) => {
    const { quoteSettings } = useDefaultQuoteSettingsContext()
    const [isUsed, setIsUsed] = useState(false)
    const [carState, setCarState] = useState(initialCarState)

    const [calcState, setCalcState] = useState(getInitialCalcState(quoteSettings))

    const [getMakesForYearResponse, getMakesForYear] = useFetchHook(fetcher.makesForYear)
    const [getModelsForMakeResponse, getModelsForMake] = useFetchHook(fetcher.modelsForMake)
    const [getTrimsForModelResponse, getTrimsForModel] = useFetchHook(fetcher.trimsForModel)
    const [getCarByVinResponse, getCarByVin] = useFetchHook(fetcher.getCarByVin)

    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)

    const closeModalById = useModalsStore((state) => state.closeModalById)

    const resetCarState = () => {
        setCarState(initialCarState)
    }

    const resetCalcState = () => {
        setCarState(initialCarState)
        setCalcState(getInitialCalcState(quoteSettings))
    }

    const getLeadId = () => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('lead_id')) {
            return urlParams.get('lead_id')
        }
    }

    const saveLocation = (location, shouldSetCustomerLocation, shouldSetDealerLocation) => {
        let updateObj = {}
        if (shouldSetCustomerLocation) {
            updateObj = {
                location: location,
                location_id: location.id,
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zipcode: location.zipcode,
                county: location.county,
            }
        }
        if (shouldSetDealerLocation) {
            updateObj = {
                ...updateObj,
                dealer_location: location,
                dealer_location_id: location.id,
                dealer_address_line_one: location.address_line_one,
                dealer_address_line_two: location.address_line_two,
                dealer_city: location.city,
                dealer_state: location.state,
                dealer_zipcode: location.zipcode,
                dealer_county: location.county
            }
        }
        if (!_.isEmpty(updateObj)) {
            setCalcState({...calcState, ...updateObj})
        }

        closeModalById("LocationModal")
    }

    useDidMountEffect(function resetStateOnIsUsedChange(){
        resetCalcState()
    }, [isUsed])

    useDidMountEffect(function getCarByVinFromBE(){
        const stripped = calcState.vin.trim()
        if (stripped.length >= 17) {
            getCarByVin(stripped, isUsed)
        }
    }, [calcState.vin])

    useDidMountEffect(function setMakes() {
        if (!getMakesForYearResponse?.loading && getMakesForYearResponse?.data?.data) {
            let makes = getMakesForYearResponse?.data?.data

            makes = _.sortBy(makes, ['name'])
            setCarState({
                ...carState,
                makes: makes,
                vin_required: null,
            })
        }
    }, [getMakesForYearResponse])

    useDidMountEffect(function setModels(){
        if (!getModelsForMakeResponse?.loading && getModelsForMakeResponse?.data?.data) {
            const models = getModelsForMakeResponse?.data?.data
            setCarState({
                ...carState,
                models: models,
                vin_required: null,
            })
        }
    }, [getModelsForMakeResponse])

    useDidMountEffect(function setTrims(){
        if (!getTrimsForModelResponse?.loading && getTrimsForModelResponse?.data?.data) {
            const trims = getTrimsForModelResponse?.data?.data
            setCarState({
                ...carState,
                trims: trims,
                vin_required: null,
            })
        }
    }, [getTrimsForModelResponse])


    useDidMountEffect(function setCarByVinFromBE(){
        if (!getCarByVinResponse?.loading && getCarByVinResponse?.data?.data) {
            const data = getCarByVinResponse?.data?.data
            let trim = data.trim || ''
            const last_deal_data = trim?.last_deal_data
            const base_car_id = trim?.id
            const options = trim.option_codes || []

            let option_codes = []
            if (options.length === 1) {
                option_codes = options
            }

            if (trim){
                trim = JSON.stringify(trim)
            }

            setCarState({
                ...carState,
                year: data.year,
                make: data.make.cleaned_name,
                model: data.model.cleaned_name,
                trim: trim,
                options: options,
                vin_vehicles: data?.vin_vehicles,

                base_car_id: base_car_id,

                makes: data.makes,
                models: data.models,
                trims: data.trims,
                option_codes: option_codes,
            })

            setCalcState({
                ...calcState,
                car_id: base_car_id,
                base_car_id: base_car_id,
                msrp: last_deal_data?.msrp,
                cost: last_deal_data?.cost,
                // profit: last_deal_data?.profit,
            })
        }
    }, [getCarByVinResponse])

    useDidMountEffect(function updateCalcStateWhenQuoteSettingsChange(){
        if (quoteSettings) {
            const customerLocation = quoteSettings.customer_location
            const dealerLocation = quoteSettings.dealer_location
            setCalcState({
                ...calcState,
                profit: quoteSettings.profit,
                drive_off: quoteSettings.drive_off,
                is_drive_off_cap_reduction: quoteSettings.is_drive_off_cap_reduction,
                mileage: quoteSettings.mileage,
                mileage_alt: quoteSettings.mileage_alt,

                location: quoteSettings.customer_location,
                location_id: quoteSettings.customer_location.id,
                address_line_one: customerLocation.address_line_one,
                address_line_two: customerLocation.address_line_two,
                city: customerLocation.city,
                state: customerLocation.state,
                zipcode: customerLocation.zipcode,
                county: customerLocation.county,

                dealer_location: quoteSettings.dealer_location,
                dealer_location_id: quoteSettings.dealer_location.id,
                dealer_address_line_one: dealerLocation.address_line_one,
                dealer_address_line_two: dealerLocation.address_line_two,
                dealer_city: dealerLocation.city,
                dealer_state: dealerLocation.state,
                dealer_zipcode: dealerLocation.zipcode,
                dealer_county: dealerLocation.county
            })
        }
    }, [quoteSettings])

    const value = {
        carState,
        setCarState,

        calcState,
        setCalcState,

        getMakesForYear,
        getMakesForYearResponse,

        getModelsForMake,
        getModelsForMakeResponse,

        getTrimsForModel,
        getTrimsForModelResponse,

        getCarByVin,
        getCarByVinResponse,

        resetCarState,
        resetCalcState,

        createDeal,
        createDealResponse,

        getLeadId,
        isUsed,
        setIsUsed,

        saveLocation,
    }

    if (!carState) return null;

    return (
        <CalcContext.Provider value={value}>
            { children }
        </CalcContext.Provider>
    )
}

export const useCalcContext = () => {
    const context = useContext(CalcContext)

    if (context === undefined) {
        throw new Error('useCalcContext must be used within CalcContext')
    }
    return context
}